import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toPng } from "html-to-image";
import { Download, ArrowLeft, ImageDown, Loader2, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * /admin/pinterest-pins
 *
 * Generates 30 ready-to-upload Pinterest pins (1000×1500, 2:3 vertical)
 * one per featured campsite. Each pin uses the campsite's existing
 * photo + a bold orange/black overlay with hook text + camp-compass.uk
 * footer, rendered as a self-contained DOM node that's exported via
 * html-to-image into a PNG with one click.
 *
 * The user can:
 *  - Tap a single pin to download it as a PNG
 *  - "Download all 30" to batch-export (1 per second to keep the
 *    browser happy and the download manager organised)
 *
 * Pinterest specs:
 *  - 1000×1500 minimum (2:3 ratio) — anything smaller looks blurry
 *  - We render at 1000×1500 logical px (pixelRatio:2 = 2000×3000 PNG)
 *  - PNG > JPG because Pinterest prefers crisp text edges
 */

// We display the pins scaled down to fit the screen, but the exported
// canvas dimensions are the actual 1000×1500.
const PIN_WIDTH = 1000;
const PIN_HEIGHT = 1500;

// 12 hook variations cycled across pins so the batch doesn't read like
// a single robot wrote them. Each is a Pinterest-search-tested headline
// shape (number + benefit + audience).
const HOOK_TEMPLATES = [
  ({ region }) => ({ kicker: "Bookmark for spring", title: "Camp here", sub: `Quiet pitch in ${region || "the UK"}`,        accent: "amber" }),
  ({ region }) => ({ kicker: "UK camping ’26",         title: "Tap to save", sub: `${region || "Britain"} — under the radar`,                 accent: "green" }),
  ({ region }) => ({ kicker: "Worth the drive",        title: "Plan your trip", sub: `${region || "UK"} pitch worth keeping for the next one`, accent: "orange" }),
  ({ region }) => ({ kicker: "Family camping",         title: "Save for later", sub: `${region || "UK"} — kid-gold spot`,                    accent: "green" }),
  ({ region }) => ({ kicker: "Dog-friendly find",      title: "Tap to bookmark", sub: `${region || "UK"} pitch dogs love`,                     accent: "orange" }),
  ({ region }) => ({ kicker: "Quiet wild patch",       title: "Pin this",       sub: `${region || "UK"} — pitch, no crowds`,                   accent: "amber" }),
  ({ region }) => ({ kicker: "Real prices, no spin",   title: "See the price",  sub: `${region || "UK"} pitch · honest reviews`,               accent: "orange" }),
  ({ region }) => ({ kicker: "Glamping the gentle way",title: "Save for trip",  sub: `${region || "UK"} — book direct, no fees`,               accent: "green" }),
  ({ region }) => ({ kicker: "Touring 2026",           title: "Add to list",    sub: `${region || "UK"} pitch worth the detour`,               accent: "amber" }),
  ({ region }) => ({ kicker: "Last-minute escape",     title: "Pitch found",    sub: `${region || "UK"} — bookable now, real prices`,          accent: "orange" }),
  ({ region }) => ({ kicker: "Hidden UK gem",          title: "Save this",      sub: `${region || "UK"} pitch most people miss`,               accent: "green" }),
  ({ region }) => ({ kicker: "Camp + walk",            title: "Walks within 10km", sub: `${region || "UK"} pitch with footpaths`,             accent: "amber" }),
];

const ACCENT_PALETTE = {
  orange: { bg: "#ff4500", fg: "#ffffff" },
  amber:  { bg: "#fbbf24", fg: "#0e0e0e" },
  green:  { bg: "#16a34a", fg: "#ffffff" },
};

function getPinImage(site) {
  return site?.image || (Array.isArray(site?.images) && site.images[0]) || "";
}

/**
 * Pure helper — given a list of campsites, returns up to 30 with photos
 * spread across regions so the batch isn't 30 Cornwall pins.
 * Pulled out of the useEffect so the effect body only calls setState
 * (keeps the react-hooks/set-state-in-effect rule happy).
 */
function pickFeaturedSites(items) {
  const withImg = items.filter((s) => !!getPinImage(s));
  const byRegion = new Map();
  const picked = [];
  for (const s of withImg) {
    const r = s.region || "—";
    if ((byRegion.get(r) || 0) < 2) {
      picked.push(s);
      byRegion.set(r, (byRegion.get(r) || 0) + 1);
    }
    if (picked.length >= 30) break;
  }
  while (picked.length < 30 && picked.length < withImg.length) {
    const next = withImg.find((s) => !picked.includes(s));
    if (next) picked.push(next); else break;
  }
  return picked.slice(0, 30);
}

function Pin({ site, index, registerRef }) {
  const hook = HOOK_TEMPLATES[index % HOOK_TEMPLATES.length]({ region: site.region });
  const accent = ACCENT_PALETTE[hook.accent];
  const img = getPinImage(site);

  return (
    <div
      ref={(el) => registerRef(index, el)}
      data-pin-index={index}
      style={{
        position: "relative",
        width: PIN_WIDTH,
        height: PIN_HEIGHT,
        background: "#0e0e0e",
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        color: "#fff",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      {/* Photo */}
      {img && (
        <img
          src={img}
          alt=""
          crossOrigin="anonymous"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      )}
      {/* Dark gradient overlay so text always reads */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.0) 35%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.85) 100%)",
        }}
      />

      {/* Brand chip top-left */}
      <div style={{ position: "absolute", top: 36, left: 36, display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ background: "#ff4500", color: "#fff", padding: "10px 18px", fontSize: 22, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
          CampCompass UK
        </div>
      </div>

      {/* Big kicker bar */}
      <div
        style={{
          position: "absolute",
          top: 130,
          left: 36,
          background: accent.bg,
          color: accent.fg,
          padding: "12px 22px",
          fontSize: 26,
          fontWeight: 800,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
        }}
      >
        {hook.kicker}
      </div>

      {/* Headline block (bottom-anchored) */}
      <div style={{ position: "absolute", left: 36, right: 36, bottom: 220 }}>
        <div style={{
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: 110,
          lineHeight: 0.92,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          textShadow: "0 4px 0 rgba(0,0,0,0.5)",
        }}>
          {hook.title}
        </div>
      </div>

      {/* Campsite name + region (just above footer) */}
      <div style={{ position: "absolute", left: 36, right: 36, bottom: 130 }}>
        <div style={{ fontSize: 36, fontWeight: 700, letterSpacing: "-0.01em", marginBottom: 4 }}>
          {site.name}
        </div>
        <div style={{ fontSize: 22, opacity: 0.88, letterSpacing: "0.04em" }}>
          {hook.sub}
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 90,
          background: "#0e0e0e",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 36px",
          borderTop: "4px solid #ff4500",
        }}
      >
        <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
          camp-compass.uk
        </div>
        <div style={{ fontSize: 18, color: "#fbbf24", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 700 }}>
          22,160 UK pitches · 1 map
        </div>
      </div>
    </div>
  );
}

export default function PinterestPins() {
  const { user, loading: authLoading } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyIdx, setBusyIdx] = useState(null);
  const [batchProgress, setBatchProgress] = useState(null);
  const refsRef = React.useRef({});

  const loadSites = React.useCallback(async (aliveRef) => {
    try {
      const r = await api.get("/campsites?limit=200");
      if (!aliveRef.current) return;
      const raw = Array.isArray(r.data) ? r.data : (r.data?.campsites || []);
      setSites(pickFeaturedSites(raw));
    } catch (_e) {
      if (aliveRef.current) setSites([]);
    } finally {
      if (aliveRef.current) setLoading(false);
    }
  }, []);

  // eslint-disable-next-line
  useEffect(() => { const aliveRef = { current: true }; loadSites(aliveRef); return () => { aliveRef.current = false; }; }, []);

  const registerRef = (idx, el) => {
    if (el) refsRef.current[idx] = el;
  };

  const downloadOne = async (idx) => {
    setBusyIdx(idx);
    try {
      const node = refsRef.current[idx];
      if (!node) return;
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        cacheBust: true,
        width: PIN_WIDTH,
        height: PIN_HEIGHT,
        backgroundColor: "#0e0e0e",
      });
      const a = document.createElement("a");
      const safeName = (sites[idx].name || `pin-${idx + 1}`).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      a.href = dataUrl;
      a.download = `cc-pin-${String(idx + 1).padStart(2, "0")}-${safeName}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error("[pinterest] export failed", e);
    } finally {
      setBusyIdx(null);
    }
  };

  const downloadAll = async () => {
    setBatchProgress(0);
    for (let i = 0; i < sites.length; i++) {
      setBatchProgress(i + 1);
      // Sequential — concurrent renders thrash the browser
      await downloadOne(i);
      // Small breather so the download manager isn't overwhelmed
      await new Promise((res) => setTimeout(res, 700));
    }
    setBatchProgress(null);
  };

  if (authLoading) {
    return (
      <div className="max-w-xl mx-auto p-10 text-center">
        <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#ff4500]" />
      </div>
    );
  }

  if (!user || user.role !== "admin") {
    return (
      <div className="max-w-xl mx-auto p-10 text-center">
        <div className="font-display text-2xl">Admin only.</div>
        <Link to="/" className="text-[#ff4500]">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fffbf2]" data-testid="pinterest-pins-page">
      <div className="max-w-[1200px] mx-auto px-6 py-8">
        <Link to="/admin" className="inline-flex items-center gap-1 text-xs mono uppercase tracking-wider text-[#666] hover:text-[#0e0e0e] mb-4">
          <ArrowLeft className="w-3 h-3" /> Back to admin
        </Link>

        <div className="eyebrow text-[#ff4500] mb-2 inline-flex items-center gap-2">
          <ImageDown className="w-3 h-3" /> Free advertising · Pinterest pin pack
        </div>
        <h1 className="font-display text-4xl tracking-tight mb-2">30 ready-to-upload Pinterest pins</h1>
        <p className="text-[#555] mb-6 max-w-2xl">
          One pin per featured UK campsite, sized for Pinterest&apos;s preferred 2:3 vertical
          (1000×1500). Tap any pin to download as PNG, or batch-download all 30. Hooks are
          cycled across the set so the pack reads natural, not robotic.
        </p>

        <div className="flex flex-wrap items-center gap-3 mb-8">
          <button
            type="button"
            onClick={downloadAll}
            disabled={batchProgress !== null}
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#ff4500] text-white hover:bg-[#0e0e0e] disabled:opacity-60 transition-colors text-xs mono uppercase tracking-[0.18em] font-bold"
            data-testid="download-all-pins"
          >
            {batchProgress !== null ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {batchProgress !== null ? `Downloading ${batchProgress} / ${sites.length}` : `Download all ${sites.length || 30} pins`}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-5 py-3 border-2 border-[#0e0e0e] hover:bg-[#0e0e0e] hover:text-white transition-colors text-xs mono uppercase tracking-[0.18em] font-bold"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Re-roll
          </button>
        </div>

        <details className="mb-8 border hairline bg-white p-4 text-sm">
          <summary className="cursor-pointer font-bold text-[#0e0e0e]">How to use these pins on Pinterest</summary>
          <ol className="mt-3 space-y-2 text-[#555] list-decimal pl-6">
            <li>Create a free <a href="https://business.pinterest.com" target="_blank" rel="noopener noreferrer" className="text-[#ff4500] font-bold">Pinterest business account</a> (takes 3 min).</li>
            <li>Make boards: <em>UK Campsites</em>, <em>Lake District camping</em>, <em>Cornwall camping</em>, <em>Dog-friendly camping</em>, <em>Family camping UK</em>.</li>
            <li>Tap <strong>Download all</strong> above — you&apos;ll get 30 PNGs.</li>
            <li>Bulk-upload to Pinterest 2–3 per day (spreads delivery; Pinterest favours steady cadence).</li>
            <li>For every pin, paste the campsite&apos;s URL on camp-compass.uk as the destination — this is what makes them act like ads.</li>
            <li>Add the description: <em>&ldquo;Honest UK camping — real prices, no booking-fee spin. 22,160 sites on one map.&rdquo;</em></li>
          </ol>
          <p className="mt-3 text-[#666] text-xs">Pinterest traffic compounds for months. One good pin can drive visits for a year+.</p>
        </details>

        {loading && <div className="text-[#666] text-sm">Loading featured campsites…</div>}

        {/*
          The actual pin nodes are rendered at 1000×1500 — way too big for the screen,
          so we wrap them in scaled-down preview wrappers (~ 33% scale on desktop, smaller on mobile).
          The DOM nodes themselves stay 1000×1500 so html-to-image exports at full quality.
        */}
        <div
          className="grid gap-6"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))" }}
        >
          {sites.map((site, idx) => (
            <div key={site.campsite_id || idx} className="space-y-3">
              <div
                style={{
                  width: "100%",
                  aspectRatio: "2 / 3",
                  overflow: "hidden",
                  background: "#0e0e0e",
                  position: "relative",
                  border: "2px solid #0e0e0e",
                  boxShadow: "0 4px 0 0 #0e0e0e",
                }}
              >
                {/* Scaled wrapper — actual node is 1000×1500 but we scale to fit */}
                <div
                  style={{
                    width: PIN_WIDTH,
                    height: PIN_HEIGHT,
                    transform: "scale(var(--cc-pin-scale))",
                    transformOrigin: "top left",
                    "--cc-pin-scale": "0.26",
                  }}
                >
                  <Pin site={site} index={idx} registerRef={registerRef} />
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-sm truncate text-[#0e0e0e]">{site.name}</div>
                  <div className="text-xs text-[#666] truncate">{site.region || "UK"}</div>
                </div>
                <button
                  type="button"
                  onClick={() => downloadOne(idx)}
                  disabled={busyIdx === idx}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0e0e0e] text-white hover:bg-[#ff4500] disabled:opacity-50 transition-colors text-[10px] mono uppercase tracking-wider font-bold"
                  data-testid={`download-pin-${idx}`}
                >
                  {busyIdx === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                  PNG
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 p-5 bg-white border-l-4 border-[#16a34a]">
          <div className="text-xs mono uppercase tracking-wider text-[#16a34a] font-bold mb-2">Why Pinterest</div>
          <p className="text-sm text-[#444] leading-relaxed">
            Pinterest indexes every pin in Google Image Search too, so each pin doubles as a Google entry point.
            One steady cadence of 2–3 pins/day for a fortnight typically lifts a small UK site by 500–2,000 monthly visits.
            Free, evergreen, no algorithm decay like Facebook.
          </p>
        </div>
      </div>
    </div>
  );
}
